/**
 * Obligations Intelligence — Intent-Based Command View
 *
 * 3 modes: Urgency | Gap Drill-Down | Register
 * API: /api/v1/obligations/*
 *
 * Satisfies 4 user journeys:
 *   Compliance Manager → Urgency Queue (morning triage)
 *   CSO / C-Suite      → Gap Drill-Down (why is X at Y%?)
 *   ESG Controller     → Urgency Queue (verify auto-resolutions)
 *   Auditor            → Full Register (coverage matrix + export)
 */
import { useState, useEffect, useCallback, useRef } from "react";

const C = {
  bg: "#050a14", surface: "#0c1220", card: "#111c30", cardHover: "#162040",
  border: "#1a2744", borderHi: "#2a3a5c", text: "#e2e8f0", muted: "#6b7a96", dim: "#384660",
  blue: "#3b82f6", amber: "#f59e0b", emerald: "#10b981", purple: "#8b5cf6",
  cyan: "#06b6d4", red: "#ef4444", teal: "#14b8a6", orange: "#f97316", white: "#fff",
};
const API = process.env.REACT_APP_BACKEND_URL || "";

async function apiFetch(p, opts) {
  try {
    const r = await fetch(`${API}${p}`, { credentials: "include", ...opts });
    if (!r.ok) return null;
    const t = await r.text();
    try { return JSON.parse(t); } catch { return null; }
  } catch { return null; }
}

const Pl = ({ color: cl, children, small, glow, onClick, testId }) => (
  <span onClick={onClick} data-testid={testId || `pill-${(children?.toString() || "").replace(/\s/g, "-").toLowerCase()}`}
    style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: small ? "1px 5px" : "2px 8px", borderRadius: 99,
      fontSize: small ? 7 : 9, fontWeight: 600, background: cl + "14", color: cl, border: `1px solid ${cl}22`,
      cursor: onClick ? "pointer" : "default", boxShadow: glow ? `0 0 6px ${cl}22` : "none" }}>
    {children}
  </span>
);

const Bar = ({ v, cl, h = 5 }) => (
  <div style={{ height: h, borderRadius: 99, background: C.border, overflow: "hidden", flex: 1 }}>
    <div style={{ width: `${Math.min(v, 100)}%`, height: "100%", borderRadius: 99, background: cl, transition: "width .4s" }} />
  </div>
);

const PRIORITY_CL = { critical: C.red, high: C.orange, medium: C.amber, low: C.dim };
const FW_ICON = {
  ESRS: "\u{1F3DB}\uFE0F", GRI: "\u{1F4CB}", TCFD: "\u{1F321}\uFE0F", CDP: "\u{1F4A7}", SBTi: "\u{1F3AF}", ISSB: "\u{1F4D0}", CBAM: "\u{1F3ED}",
  Governance: "\u2696\uFE0F", SFDR: "\u{1F4D1}", "EU Taxonomy": "\u{1F33F}", CSDDD: "\u{1F517}", TNFD: "\u{1F333}", SASB: "\u{1F4CA}", "GHG Protocol": "\u{1F4A8}",
};
const FW_NAV = {
  ESRS: "csrd", GRI: "voluntary", TCFD: "voluntary", CDP: "voluntary", SBTi: "voluntary",
  SASB: "voluntary", TNFD: "voluntary", ISSB: "issb", CBAM: "cbam", Governance: "governance",
  SFDR: "sfdr", "EU Taxonomy": "taxonomy", CSDDD: "csddd", "GHG Protocol": "voluntary",
};

export default function ObligationsIntelligence({ onNavigate, initialFramework, initialMode }) {
  const [mode, setMode] = useState(initialMode || (initialFramework ? "gaps" : "urgent"));
  const [data, setData] = useState(null);
  const [gapFw, setGapFw] = useState(initialFramework || "ESRS");
  const [gapData, setGapData] = useState(null);
  const [regData, setRegData] = useState(null);
  const [expandedOb, setExpandedOb] = useState(null);
  const [trailData, setTrailData] = useState(null);
  const [fwFilter, setFwFilter] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [entityFilter, setEntityFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [reassignId, setReassignId] = useState(null);
  const reassignRef = useRef(null);
  const deadlineRef = useRef(null);

  const go = useCallback((p) => { if (onNavigate) onNavigate(p); }, [onNavigate]);

  const fetchUrgent = useCallback(async () => {
    setLoading(true);
    let url = "/api/v1/obligations/urgent?status=open";
    if (fwFilter) url += `&framework=${fwFilter}`;
    if (ownerFilter) url += `&owner=${ownerFilter}`;
    if (entityFilter) url += `&entity=${entityFilter}`;
    const d = await apiFetch(url);
    setData(d);
    setLoading(false);
  }, [fwFilter, ownerFilter, entityFilter]);

  const fetchGaps = useCallback(async (fw) => {
    setLoading(true);
    const d = await apiFetch(`/api/v1/obligations/gaps/${fw}`);
    setGapData(d);
    setLoading(false);
  }, []);

  const fetchRegister = useCallback(async () => {
    setLoading(true);
    const d = await apiFetch("/api/v1/obligations/register");
    setRegData(d);
    setLoading(false);
  }, []);

  const fetchTrail = useCallback(async (obId) => {
    const d = await apiFetch(`/api/v1/obligations/${obId}/trail`);
    setTrailData(d);
  }, []);

  const refreshCurrent = useCallback(() => {
    if (mode === "urgent") fetchUrgent();
    else if (mode === "gaps") fetchGaps(gapFw);
    else fetchRegister();
  }, [mode, fetchUrgent, fetchGaps, fetchRegister, gapFw]);

  useEffect(() => {
    refreshCurrent();
  }, [refreshCurrent]);

  const doAction = useCallback(async (obId, action, body = {}) => {
    await apiFetch(`/api/v1/obligations/${obId}/${action}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    refreshCurrent();
  }, [refreshCurrent]);

  const handleReassign = useCallback((obId) => {
    const newOwner = reassignRef.current?.value?.trim();
    const newDeadline = deadlineRef.current?.value?.trim();
    if (!newOwner) return;
    const body = { owner: newOwner, assigned_by: "user" };
    if (newDeadline) body.due_date = newDeadline;
    doAction(obId, "assign", body);
    setReassignId(null);
  }, [doAction]);

  const toggleExpand = useCallback((obId) => {
    if (expandedOb === obId) { setExpandedOb(null); setTrailData(null); setReassignId(null); }
    else { setExpandedOb(obId); setReassignId(null); fetchTrail(obId); }
  }, [expandedOb, fetchTrail]);

  const urgent = data?.obligations || [];
  const frameworks = [...new Set(urgent.map((o) => o.framework))].sort();
  const owners = [...new Set(urgent.map((o) => o.owner))].sort();
  const entities = [...new Set(urgent.map((o) => o.entity_name || o.entity_id).filter(Boolean))].sort();

  // ── OBLIGATION ROW (compact + expandable) ──
  const ObRow = ({ ob }) => {
    const pcl = PRIORITY_CL[ob.priority] || C.dim;
    const days = ob.days_remaining ?? 999;
    const overdue = days < 0;
    const expanded = expandedOb === ob.id;
    const evCount = (ob.linked_evidence || []).length;
    const progDone = ob.progress?.done ?? 0;
    const progTotal = ob.progress?.total ?? 0;

    return (
      <div style={{ marginBottom: 3 }} data-testid={`ob-row-${ob.id}`}>
        {/* Compact row: priority, deadline, title, requirement, framework, owner, evidence count */}
        <div onClick={() => toggleExpand(ob.id)} data-testid={`ob-toggle-${ob.id}`}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "7px 10px",
            borderRadius: expanded ? "6px 6px 0 0" : 6, background: expanded ? C.cardHover : C.card,
            border: `1px solid ${overdue ? C.red + "33" : pcl + "15"}`,
            borderBottom: expanded ? "none" : undefined, cursor: "pointer", transition: "all .15s" }}>
          <Pl color={pcl} small>{ob.priority}</Pl>
          {overdue && <Pl color={C.red} small glow>{Math.abs(days)}d overdue</Pl>}
          {!overdue && days <= 7 && <Pl color={C.amber} small>{days}d left</Pl>}
          {!overdue && days > 7 && days < 999 && <Pl color={C.dim} small>{days}d</Pl>}
          <span style={{ fontSize: 10, fontWeight: 600, flex: 1 }}>{ob.title}</span>
          <Pl color={C.blue} small>{ob.framework} {ob.requirement_code ? `· ${ob.requirement_code}` : ""}</Pl>
          <span style={{ fontSize: 8, color: C.muted }}>{ob.owner}</span>
          {evCount > 0
            ? <Pl color={C.teal} small testId={`ev-count-${ob.id}`}>{evCount} ev</Pl>
            : <Pl color={C.red} small testId={`ev-count-${ob.id}`}>0 ev</Pl>}
          {ob.escalated && <Pl color={C.red} small glow>escalated</Pl>}
          <span style={{ fontSize: 9, color: C.dim }}>{expanded ? "\u25B2" : "\u25BC"}</span>
        </div>

        {/* Expanded: context panel + lifecycle + actions */}
        {expanded && (
          <div style={{ padding: "10px 12px", background: C.surface, border: `1px solid ${pcl}15`,
            borderTop: "none", borderRadius: "0 0 6px 6px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              {/* CONTEXT PANEL */}
              <div>
                <div style={{ fontSize: 8, fontWeight: 700, color: C.muted, marginBottom: 3, letterSpacing: "0.5px" }}>CONTEXT PANEL</div>
                <div style={{ fontSize: 9, lineHeight: 1.6, color: C.text, marginBottom: 4 }}>{ob.description || "No description"}</div>
                <div style={{ fontSize: 8, color: C.dim, lineHeight: 1.8 }}>
                  <div>Entity: <strong style={{ color: C.text }}>{ob.entity_name || ob.entity_id}</strong></div>
                  <div>Framework requirement: <strong style={{ color: C.blue }}>{ob.framework} {ob.requirement_code}</strong></div>
                  <div>Due: <strong style={{ color: overdue ? C.red : C.text }}>{ob.due_date || "No deadline"}</strong></div>
                  <div>Owner: <strong style={{ color: C.text }}>{ob.owner}</strong></div>
                  {ob.escalated && <div>Escalated to: <strong style={{ color: C.red }}>{(ob.watchers || []).join(", ") || "CSO"}</strong></div>}
                </div>
                {progTotal > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 6 }}>
                    <span style={{ fontSize: 8, color: C.muted }}>Evidence gap:</span>
                    <Bar v={(progDone / Math.max(progTotal, 1)) * 100}
                      cl={progDone === progTotal ? C.emerald : C.amber} />
                    <span style={{ fontSize: 8, fontWeight: 700 }}>{progDone}/{progTotal}</span>
                  </div>
                )}
                {evCount > 0 && (
                  <div style={{ fontSize: 8, color: C.teal, marginTop: 3 }}>Linked evidence: {ob.linked_evidence.join(", ")}</div>
                )}
                {evCount === 0 && (
                  <div style={{ fontSize: 8, color: C.red, marginTop: 3, fontWeight: 600 }}>No evidence linked</div>
                )}
              </div>

              {/* LIFECYCLE TIMELINE */}
              <div>
                <div style={{ fontSize: 8, fontWeight: 700, color: C.muted, marginBottom: 3, letterSpacing: "0.5px" }}>LIFECYCLE TIMELINE</div>
                {trailData?.trail?.length > 0 ? trailData.trail.map((t, i) => (
                  <div key={i} style={{ display: "flex", gap: 4, padding: "3px 0", fontSize: 8,
                    borderBottom: i < (trailData.trail.length - 1) ? `1px solid ${C.border}` : "none" }}>
                    <span style={{ color: C.amber, minWidth: 55, fontFamily: "monospace" }}>{t.timestamp?.slice(0, 10) || ""}</span>
                    <Pl color={
                      t.action === "created" ? C.blue :
                      t.action === "assigned" ? C.cyan :
                      t.action === "resolved" || t.action === "auto_resolved" ? C.emerald :
                      t.action === "escalated" ? C.red :
                      t.action === "evidence_linked" ? C.teal :
                      C.dim} small>{t.action.replace(/_/g, " ")}</Pl>
                    <span style={{ color: C.muted, flex: 1 }}>{t.actor}</span>
                    {t.details?.note && <span style={{ color: C.dim, fontStyle: "italic" }}>{t.details.note}</span>}
                  </div>
                )) : <div style={{ fontSize: 8, color: C.dim, padding: "4px 0" }}>Loading timeline...</div>}
              </div>
            </div>

            {/* AVAILABLE ACTIONS — matching spec: Open module, Reassign, Upload evidence, Escalate */}
            <div style={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "center" }}>
              <button data-testid={`ob-open-${ob.id}`} onClick={() => go(ob.nav_target || FW_NAV[ob.framework])}
                style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.blue}33`,
                  background: C.blue + "08", color: C.blue, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>
                \u2192 Open in {ob.framework} module
              </button>
              <button data-testid={`ob-reassign-${ob.id}`} onClick={() => setReassignId(reassignId === ob.id ? null : ob.id)}
                style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.purple}33`,
                  background: reassignId === ob.id ? C.purple + "18" : "transparent", color: C.purple, fontSize: 8, cursor: "pointer" }}>
                \u21BB Reassign owner
              </button>
              <button data-testid={`ob-evidence-${ob.id}`} onClick={() => go("evidence")}
                style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.teal}33`,
                  background: "transparent", color: C.teal, fontSize: 8, cursor: "pointer" }}>
                {"\u{1F4CE}"} Upload evidence
              </button>
              <button data-testid={`ob-escalate-${ob.id}`}
                onClick={() => doAction(ob.id, "escalate", { escalated_by: "user", watcher: "CSO" })}
                style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.red}33`,
                  background: "transparent", color: C.red, fontSize: 8, cursor: "pointer" }}>
                \u2191 Escalate to CSO
              </button>
              {ob.status === "open" && (
                <button data-testid={`ob-resolve-${ob.id}`}
                  onClick={() => doAction(ob.id, "resolve", { resolved_by: "user", method: "manual" })}
                  style={{ padding: "3px 8px", borderRadius: 4, border: `1px solid ${C.emerald}33`,
                    background: "transparent", color: C.emerald, fontSize: 8, cursor: "pointer" }}>
                  \u2713 Resolve
                </button>
              )}
            </div>

            {/* REASSIGN INLINE FORM */}
            {reassignId === ob.id && (
              <div data-testid={`reassign-form-${ob.id}`}
                style={{ display: "flex", gap: 4, alignItems: "center", marginTop: 6, padding: "6px 8px",
                  borderRadius: 4, background: C.card, border: `1px solid ${C.purple}22` }}>
                <span style={{ fontSize: 8, color: C.muted }}>New owner:</span>
                <input ref={reassignRef} data-testid={`reassign-owner-input-${ob.id}`}
                  defaultValue={ob.owner} placeholder="Owner name"
                  style={{ padding: "2px 6px", borderRadius: 3, border: `1px solid ${C.border}`,
                    background: C.surface, color: C.text, fontSize: 9, width: 120 }} />
                <span style={{ fontSize: 8, color: C.muted }}>Deadline:</span>
                <input ref={deadlineRef} type="date" data-testid={`reassign-deadline-input-${ob.id}`}
                  defaultValue={ob.due_date}
                  style={{ padding: "2px 6px", borderRadius: 3, border: `1px solid ${C.border}`,
                    background: C.surface, color: C.text, fontSize: 9 }} />
                <button data-testid={`reassign-submit-${ob.id}`} onClick={() => handleReassign(ob.id)}
                  style={{ padding: "2px 8px", borderRadius: 3, border: "none",
                    background: C.purple, color: C.white, fontSize: 8, fontWeight: 600, cursor: "pointer" }}>
                  Assign
                </button>
                <button onClick={() => setReassignId(null)}
                  style={{ padding: "2px 6px", borderRadius: 3, border: "none",
                    background: "transparent", color: C.muted, fontSize: 8, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="obligations-intelligence">
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 800 }} data-testid="obligations-header">
          \u26A1 Obligations Intelligence
        </div>
        <div style={{ flex: 1 }} />
        {data && <Pl color={data.overdue_count > 0 ? C.red : C.emerald} small glow testId="overdue-badge">{data.overdue_count || 0} overdue</Pl>}
        {data && <Pl color={C.amber} small testId="open-badge">{data.total || 0} open</Pl>}
      </div>

      {/* Mode tabs */}
      <div style={{ display: "flex", gap: 3, marginBottom: 10 }} data-testid="mode-tabs">
        {[
          { id: "urgent", l: "What's urgent", i: "\u{1F525}", desc: "Priority queue" },
          { id: "gaps", l: "Gap drill-down", i: "\u{1F4C9}", desc: "Why is X at Y%?" },
          { id: "register", l: "Full register", i: "\u{1F4CB}", desc: "Audit inventory" },
        ].map((m) => (
          <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
            style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6,
              fontSize: 9, fontWeight: 600, cursor: "pointer",
              border: `1px solid ${mode === m.id ? C.blue : C.border}`,
              background: mode === m.id ? C.blue + "12" : "transparent",
              color: mode === m.id ? C.blue : C.muted }}>
            {m.i} {m.l}
          </button>
        ))}
      </div>

      {/* ═══ MODE 1: URGENCY QUEUE ═══ */}
      {mode === "urgent" && (
        <div data-testid="urgency-view">
          {/* Filters: Framework + Entity + Owner */}
          <div style={{ display: "flex", gap: 4, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 8, color: C.dim, fontWeight: 600 }}>FILTER:</span>
            <select data-testid="filter-framework" value={fwFilter} onChange={(e) => setFwFilter(e.target.value)}
              style={{ padding: "3px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 9 }}>
              <option value="">All frameworks</option>
              {frameworks.map((fw) => <option key={fw} value={fw}>{fw}</option>)}
            </select>
            <select data-testid="filter-entity" value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)}
              style={{ padding: "3px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 9 }}>
              <option value="">All entities</option>
              {entities.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <select data-testid="filter-owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}
              style={{ padding: "3px 6px", borderRadius: 4, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontSize: 9 }}>
              <option value="">All owners</option>
              {owners.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            {(fwFilter || ownerFilter || entityFilter) && (
              <button data-testid="clear-filters" onClick={() => { setFwFilter(""); setOwnerFilter(""); setEntityFilter(""); }}
                style={{ padding: "2px 6px", borderRadius: 4, border: "none", background: C.red + "22", color: C.red, fontSize: 8, cursor: "pointer" }}>
                \u2715 Clear
              </button>
            )}
          </div>

          {/* Overdue section */}
          {urgent.filter((o) => o.overdue).length > 0 && (
            <div style={{ marginBottom: 8 }} data-testid="overdue-section">
              <div style={{ fontSize: 9, fontWeight: 700, color: C.red, marginBottom: 4 }}>
                {"\u{1F6A8}"} OVERDUE ({urgent.filter((o) => o.overdue).length})
              </div>
              {urgent.filter((o) => o.overdue).map((ob) => <ObRow key={ob.id} ob={ob} />)}
            </div>
          )}

          {/* Due this week */}
          {urgent.filter((o) => !o.overdue && o.days_remaining <= 7).length > 0 && (
            <div style={{ marginBottom: 8 }} data-testid="due-soon-section">
              <div style={{ fontSize: 9, fontWeight: 700, color: C.amber, marginBottom: 4 }}>
                \u26A0\uFE0F DUE THIS WEEK ({urgent.filter((o) => !o.overdue && o.days_remaining <= 7).length})
              </div>
              {urgent.filter((o) => !o.overdue && o.days_remaining <= 7).map((ob) => <ObRow key={ob.id} ob={ob} />)}
            </div>
          )}

          {/* Upcoming */}
          {urgent.filter((o) => !o.overdue && o.days_remaining > 7).length > 0 && (
            <div data-testid="upcoming-section">
              <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
                UPCOMING ({urgent.filter((o) => !o.overdue && o.days_remaining > 7).length})
              </div>
              {urgent.filter((o) => !o.overdue && o.days_remaining > 7).map((ob) => <ObRow key={ob.id} ob={ob} />)}
            </div>
          )}

          {!loading && urgent.length === 0 && (
            <div data-testid="empty-state" style={{ padding: 20, textAlign: "center", color: C.emerald, fontSize: 12 }}>
              \u2713 No open obligations matching your filters
            </div>
          )}
        </div>
      )}

      {/* ═══ MODE 2: GAP DRILL-DOWN ═══ */}
      {mode === "gaps" && (
        <div data-testid="gaps-view">
          <div style={{ display: "flex", gap: 3, marginBottom: 10, flexWrap: "wrap" }}>
            {Object.keys(FW_ICON).map((fw) => (
              <button key={fw} data-testid={`gap-fw-${fw}`} onClick={() => { setGapFw(fw); fetchGaps(fw); }}
                style={{ padding: "3px 8px", borderRadius: 4, fontSize: 8, fontWeight: 600, cursor: "pointer",
                  border: `1px solid ${gapFw === fw ? C.blue : C.border}`,
                  background: gapFw === fw ? C.blue + "12" : "transparent",
                  color: gapFw === fw ? C.blue : C.muted }}>
                {FW_ICON[fw]} {fw}
              </button>
            ))}
          </div>

          {gapData && (
            <div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                <div data-testid="readiness-hero" style={{ padding: "12px 16px", borderRadius: 8, background: C.surface,
                  border: `1px solid ${gapData.readiness_pct >= 70 ? C.emerald : gapData.readiness_pct >= 40 ? C.amber : C.red}22`,
                  textAlign: "center", minWidth: 100 }}>
                  <div style={{ fontSize: 24, fontWeight: 800,
                    color: gapData.readiness_pct >= 70 ? C.emerald : gapData.readiness_pct >= 40 ? C.amber : C.red }}>
                    {gapData.readiness_pct}%
                  </div>
                  <div style={{ fontSize: 8, color: C.muted }}>readiness</div>
                </div>
                <div style={{ flex: 1, padding: "10px 12px", borderRadius: 8, background: C.surface, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, fontWeight: 600, marginBottom: 4 }} data-testid="gap-story">{gapData.story}</div>
                  <div style={{ display: "flex", gap: 8, fontSize: 8 }}>
                    <span>Requirements: <strong>{gapData.requirements_complete}/{gapData.requirements_total}</strong></span>
                    <span>Gaps: <strong style={{ color: C.red }}>{gapData.gap_count}</strong></span>
                    <span>Open obligations: <strong style={{ color: C.amber }}>{gapData.obligation_count}</strong></span>
                  </div>
                </div>
                <button data-testid="open-fw-module" onClick={() => go(FW_NAV[gapFw])}
                  style={{ padding: "8px 14px", borderRadius: 6, border: `1px solid ${C.blue}33`,
                    background: C.blue + "08", color: C.blue, fontSize: 9, fontWeight: 600, cursor: "pointer", alignSelf: "center" }}>
                  \u2192 Open {gapFw} module
                </button>
              </div>

              {gapData.gaps?.length > 0 && (
                <div style={{ marginBottom: 10 }} data-testid="gap-cards">
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.red, marginBottom: 4 }}>
                    REQUIREMENT GAPS ({gapData.gap_count})
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 4 }}>
                    {gapData.gaps.map((g, i) => (
                      <div key={i} data-testid={`gap-card-${g.code}`} style={{ padding: "8px 10px", borderRadius: 5, background: C.card,
                        border: `1px solid ${g.status === "gap" ? C.red : C.amber}18` }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                          <span style={{ fontSize: 10, fontWeight: 700 }}>{g.code}</span>
                          <Pl color={g.status === "gap" ? C.red : C.amber} small>{g.status}</Pl>
                        </div>
                        <div style={{ color: C.muted, fontSize: 8 }}>
                          DPs: {g.approved}/{g.datapoints} approved{"\u00B7"} Evidence: {g.evidence} ({g.verified} verified)
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {gapData.obligations?.length > 0 && (
                <div data-testid="gap-obligations">
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.amber, marginBottom: 4 }}>
                    OBLIGATIONS CLOSING GAPS ({gapData.obligation_count})
                  </div>
                  {gapData.obligations.map((ob) => <ObRow key={ob.id} ob={ob} />)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ MODE 3: FULL REGISTER ═══ */}
      {mode === "register" && regData && (
        <div data-testid="register-view">
          <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
            {[
              { l: "Total", v: regData.total, c: C.text },
              { l: "Open", v: regData.by_status?.open || 0, c: C.amber },
              { l: "Resolved", v: regData.by_status?.resolved || 0, c: C.emerald },
              { l: "Escalated", v: regData.by_status?.escalated || 0, c: C.red },
            ].map((s, i) => (
              <div key={i} data-testid={`stat-${s.l.toLowerCase()}`}
                style={{ padding: "8px 12px", borderRadius: 6, background: C.surface,
                  border: `1px solid ${s.c}15`, textAlign: "center", minWidth: 70 }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: s.c }}>{s.v}</div>
                <div style={{ fontSize: 7, color: C.muted }}>{s.l}</div>
              </div>
            ))}
          </div>

          {regData.coverage_matrix?.length > 0 && (
            <div style={{ marginBottom: 10 }} data-testid="coverage-matrix">
              <div style={{ fontSize: 9, fontWeight: 700, color: C.cyan, marginBottom: 4 }}>
                COVERAGE MATRIX (Framework \u00D7 Entity)
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))", gap: 3 }}>
                {regData.coverage_matrix.map((c, i) => (
                  <div key={i} style={{ padding: "6px 8px", borderRadius: 4, background: C.card,
                    border: `1px solid ${c.open > 0 ? C.amber : C.emerald}12`, fontSize: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontWeight: 600 }}>{c.framework}</span>
                      <Pl color={c.open > 0 ? C.amber : C.emerald} small>{c.open} open / {c.total}</Pl>
                    </div>
                    <div style={{ color: C.muted, fontSize: 7 }}>{c.entity_name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
            ALL OBLIGATIONS ({regData.total})
          </div>
          {regData.obligations?.map((ob) => <ObRow key={ob.id} ob={ob} />)}
        </div>
      )}
    </div>
  );
}
